import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { contactRoles, singleUseRoles } from '@shared/constants/contact-roles';
import { contactRequirementsByGroupTypes } from '@shared/constants/contact-requirements-by-group-types';
import { Contact } from '@shared/models/contact';
import { ContactRole } from '@shared/types/contact-role';
import { GroupType } from '@shared/types/group-type';
import { Country } from '@shared/models/country';
import { ModalController } from '@ionic/angular';
import { AddEditContactComponent } from '../../components/add-edit-contact/add-edit-contact.component';
import { map, Observable, startWith, take } from 'rxjs';
import { SessionService } from '@shared/services/session.service';
import { UserType } from '@shared/types/user-type';

@Component({
  selector: 'app-contacts-container',
  templateUrl: './contacts-container.component.html',
  styleUrl: './contacts-container.component.scss',
  standalone: false,
})
export class ContactsContainerComponent implements OnInit, OnChanges {
  @Input() groupType: GroupType;
  @Input() parentGroupType: GroupType;
  @Input() groupName: string;
  @Input() country: Country;
  @Input() contactForms: FormGroup;
  @Input() groupEdit: boolean = false;
  @Input() errors: any;
  @Input() formSubmitted: boolean = false;
  @Input() reviewMode?: boolean;
  @Output() resendEmailConfirmation = new EventEmitter();
  @Output() updateAllCustomers = new EventEmitter();
  @Output() hasContactsErrors = new EventEmitter<boolean>();

  contactRequirementsByGroupType = contactRequirementsByGroupTypes[0];
  requiredRoleUnassigned: boolean = false;
  hasContactErrors: boolean = false;
  optionalContactRoles: ContactRole[];
  contactFormGroups$: Observable<FormGroup[]>;
  contactsFormArray: FormArray;
  userType$: Observable<UserType | undefined>;

  constructor(
    private modalController: ModalController,
    private sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    this.userType$ = this.sessionService.getUserType();
    this.contactsFormArray = this.contactForms.get('contacts') as FormArray;

    this.contactFormGroups$ = this.contactForms.valueChanges.pipe(
      startWith(null),
      map(() => {
        this.requiredRoleUnassigned = this.hasUnassignedRequiredRole();

        const formGroups = (this.contactForms.get('contacts') as FormArray).controls as FormGroup[];
        this.checkForContactErrors(formGroups);

        return formGroups;
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formSubmitted'] && changes['formSubmitted'].currentValue === true) {
      this.markAllContactsTouched();

      const formGroups = (this.contactForms.get('contacts') as FormArray).controls as FormGroup[];
      this.checkForContactErrors(formGroups);
    }
  }

  markAllContactsTouched(): void {
    const contactsArray = this.contactForms.get('contacts') as FormArray;
    contactsArray.controls.forEach((control) => {
      const formGroup = control as FormGroup;
      Object.keys(formGroup.controls).forEach((key) => {
        formGroup.get(key)?.markAsTouched();
      });
      formGroup.markAsTouched();
    });
  }

  checkForContactErrors(formGroups: FormGroup[]): void {
    this.hasContactErrors = formGroups.some((form) => {
      this.hasContactsErrors.emit(form.invalid && (form.touched || this.formSubmitted));
      return form.invalid && (form.touched || this.formSubmitted);
    });
  }

  addContactFromHeader(): void {
    const contactFormGroups = (this.contactForms.get('contacts') as FormArray).controls as FormGroup[];
    this.addEditContact(contactFormGroups.length === 0 ? 'primary' : undefined);
  }

  async addEditContact(defaultRole?: ContactRole | FormGroup, contactForm?: FormGroup) {
    if (defaultRole && typeof defaultRole === 'object') {
      contactForm = defaultRole;
      defaultRole = undefined;
    }

    this.userType$.pipe(take(1)).subscribe((userType) => {
      this.presentAddEditContactModal(userType!, contactForm, defaultRole as ContactRole);
    });
  }

  async presentAddEditContactModal(userType: UserType, contactForm?: FormGroup, defaultRole?: ContactRole) {
    const isEdit = !!contactForm;
    const existingContacts = this.contactForms.value.contacts || [];
    const availableContactRoles = contactRoles
      .filter(
        (contactRole) =>
          (!singleUseRoles.includes(contactRole.reference) ||
            !existingContacts.some((contact: Contact) => contact.contact_roles?.includes(contactRole.reference))) &&
          !this.contactRequirementsByGroupType.excludedContactRoles.includes(contactRole.reference) &&
          !(
            this.parentGroupType === 'distributor' &&
            this.groupType == 'customer' &&
            contactRole.reference == 'partner'
          ),
      )
      .map((contactRole) => contactRole.reference);

    const modal = await this.modalController.create({
      component: AddEditContactComponent,
      componentProps: {
        groupType: this.groupType,
        contactForm: contactForm,
        country: this.country,
        errors: this.errors,
        availableContactRoles: availableContactRoles,
        isEdit: isEdit,
        userType: userType,
        defaultRole: defaultRole,
      },
      backdropDismiss: false,
      id: 'add-contact-modal',
    });
    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data && (data.contactForm || data.removeContact)) {
      if (data.removeContact) {
        const index = (this.contactForms.get('contacts') as FormArray).controls.indexOf(contactForm!);
        if (index !== -1) {
          (this.contactForms.get('contacts') as FormArray).removeAt(index);
        }
      } else {
        isEdit
          ? contactForm.patchValue(data.contactForm.value)
          : (this.contactForms.get('contacts') as FormArray).push(data.contactForm);
      }
      (this.contactForms.get('contacts') as FormArray).updateValueAndValidity();
      (this.contactForms.get('contacts') as FormArray).markAsDirty();
      (this.contactForms.get('contacts') as FormArray).markAsTouched();

      this.requiredRoleUnassigned = this.hasUnassignedRequiredRole();

      const formGroups = (this.contactForms.get('contacts') as FormArray).controls as FormGroup[];
      this.checkForContactErrors(formGroups);
    }
  }

  hasUnassignedRequiredRole() {
    const requiredRoles = contactRequirementsByGroupTypes.find(
      (groupTypeData) => groupTypeData.groupType === this.groupType,
    )?.requiredContactRoles;
    const contactFormGroups = (this.contactForms.get('contacts') as FormArray).controls as FormGroup[];

    return (
      requiredRoles?.some(
        (role) => !contactFormGroups.some((form) => form.controls['contact_roles'].value.includes(role)),
      ) ||
      (contactFormGroups.length === 0 && this.formSubmitted) ||
      false
    );
  }
}
